import { useState } from "react";
import { useChronicBillingLines } from "../../../../hooks/useChronicBillingLines";
import { API } from "../../../../services/api";
import { chronicProductEventDescription, existingControleChronicProductIds, manualChronicProductEventDescription, resolveChronicMedicamentId } from "../../../../utils/chronicBilling";
import { ADMIN_API_CONFIG } from "../controleConstants";
import { dateInputValue, todayISO } from "../controleFormat";

const NEVER_FROZEN = () => false;

function addBillingEvent(patientId, date, description) {
    return API.post(`/administration/patients/${patientId}/billing-events`, { type: "CHRONIC", date, description }, ADMIN_API_CONFIG);
}

// Choix des produits à facturer : saisie manuelle ou sélection parmi les traitements du patient.
function useChronicProductPicker({ loadPatientDetail, selectedPatient }, { isFrozen, onError }) {
    const [name, setName] = useState("");
    const [quantity, setQuantity] = useState("");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [productsLoadingMore, setProductsLoadingMore] = useState(false);
    const [productsPage, setProductsPage] = useState(0);
    const [productsTotalPages, setProductsTotalPages] = useState(0);
    const [productsError, setProductsError] = useState("");
    const [selectedProductIds, setSelectedProductIds] = useState([]);

    const loadProducts = async (patientId, nextPage = 0) => {
        if (!patientId) return;
        const setPageLoading = nextPage === 0 ? setProductsLoading : setProductsLoadingMore;
        setPageLoading(true);
        setProductsError("");
        try {
            const response = await API.get(`/medicaments/patients/${patientId}/traitements?page=${nextPage}&size=100`, ADMIN_API_CONFIG);
            const data = response.data || {};
            const content = Array.isArray(data) ? data : data.content || [];
            setProducts(current => nextPage === 0 ? content : [...current, ...content]);
            setProductsPage(data.number ?? nextPage);
            setProductsTotalPages(data.totalPages ?? (content.length > 0 ? nextPage + 1 : nextPage));
        } catch (requestError) {
            setProductsError(requestError.response?.data?.message || "Impossible de charger les produits du patient.");
        } finally {
            setPageLoading(false);
        }
    };

    const openModal = patient => {
        if (!patient?.patientId || isFrozen(patient.patientId)) return undefined;
        setShowModal(true);
        setSelectedProductIds([]);
        setProductsError("");
        return loadProducts(patient.patientId, 0);
    };

    const toggleProduct = productId => {
        if (existingControleChronicProductIds(selectedPatient).has(String(productId))) return;
        setSelectedProductIds(current => (
            current.includes(productId) ? current.filter(id => id !== productId) : [...current, productId]
        ));
    };

    const addManual = async patient => {
        const productName = name.trim();
        if (!patient?.patientId || isFrozen(patient.patientId) || !productName) return;
        setSaving(true);
        onError("");
        try {
            await addBillingEvent(patient.patientId, todayISO(), manualChronicProductEventDescription({ name: productName, quantity, reason }));
            setName("");
            setQuantity("");
            setReason("");
            setShowModal(false);
            await loadPatientDetail(patient.patientId);
        } catch (requestError) {
            onError(requestError.response?.data?.message || "Impossible d'ajouter cette facturation chronique.");
        } finally {
            setSaving(false);
        }
    };

    const addSelected = async patient => {
        const existingIds = existingControleChronicProductIds(patient);
        const chosen = products.filter(product => selectedProductIds.includes(product.id) && !existingIds.has(String(product.id)));
        if (!patient?.patientId || isFrozen(patient.patientId) || chosen.length === 0) return;
        setSaving(true);
        setProductsError("");
        try {
            await Promise.all(chosen.map(product => addBillingEvent(
                patient.patientId,
                dateInputValue(product.dateReception) || todayISO(),
                chronicProductEventDescription(product)
            )));
            setSelectedProductIds([]);
            setShowModal(false);
            await loadPatientDetail(patient.patientId);
        } catch (requestError) {
            setProductsError(requestError.response?.data?.message || "Impossible d'ajouter ces produits.");
        } finally {
            setSaving(false);
        }
    };

    return {
        name, setName, quantity, setQuantity, reason, setReason, saving,
        showModal, setShowModal, openModal,
        products, productsLoading, productsLoadingMore, productsPage, productsTotalPages, productsError, loadProducts,
        selectedProductIds, toggleProduct, addManual, addSelected,
    };
}

export function useChronicBilling(patients, { user, isFrozen = NEVER_FROZEN, onError }) {
    const lines = useChronicBillingLines({
        user,
        reload: patients.loadPatientDetail,
        apiConfig: ADMIN_API_CONFIG,
        resolveMedicamentId: resolveChronicMedicamentId,
        isFrozen,
        onError,
    });
    return { ...useChronicProductPicker(patients, { isFrozen, onError }), ...lines };
}
